import { useCallback, useEffect, useState } from 'react';

import { reloadPage } from '../../../utils/react/lazyRetry';
import { APP_VERSION_CHECK_INTERVAL_MS } from './constants';

type VersionPayload = {
  version?: string;
};

const getBuildVersion = (): string | undefined => process.env.REACT_APP_BUILD_VERSION;

const fetchDeployedVersion = async (): Promise<string | undefined> => {
  const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });

  if (!response.ok) {
    return undefined;
  }

  const payload = (await response.json()) as VersionPayload;
  return payload.version;
};

export const useAppVersionCheck = (): {
  reloadApp: () => void;
  updateAvailable: boolean;
} => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const reloadApp = useCallback((): void => {
    reloadPage();
  }, []);

  useEffect(() => {
    const buildVersion = getBuildVersion();

    if (!buildVersion || process.env.NODE_ENV === 'development') {
      return undefined;
    }

    let cancelled = false;

    const checkVersion = async (): Promise<void> => {
      try {
        const deployedVersion = await fetchDeployedVersion();

        if (!cancelled && deployedVersion && deployedVersion !== buildVersion) {
          setUpdateAvailable(true);
        }
      } catch {
        // Ignore transient network errors; next poll will retry.
      }
    };

    void checkVersion();
    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, APP_VERSION_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return { reloadApp, updateAvailable };
};
